export type Image = {
	image: any;
	type: ImageTypeEnum;
}

export enum ImageTypeEnum {
	Portrait,
	IdPhotoFront,
	IdPhotoBack,
	IdPhotoFace,
	IdSignature,
	Signature,
	TamperingFrontEvidence,
	TamperingBackEvidence,
}

export class SavedErrors {
}

export type Data = {
	ref?: string;
	sessionId: string;
	firstName: string;
	lastName: string;
	fullName: string;
	nationality: string;
	nationalityNonMRZValue: string | null;
	countryCode: string | null;
	countryCodeNonMRZValue: string | null;
	dateOfBirth: string;
	placeOfBirth: string | null;
	idNumber: string;
	idBarcode: string | null;
	idGroupName?: string;
	dateOfIssue: string | null;
	dateOfExpiration: string;
	sex: string; //yes please
	address: string;
	imageList: Image[];
	userConfirmedValues: any;
	barcodeTemplateName: string;
	barcodeTemplateType: string;
	ocrTemplateName: string;
	ocrTemplateType: string;
	idPhotoMatchLevel: number;
	ageEstimate: number;
	browser: string | null; //userAgent
	deviceModel: string | null; //deviceModel
	deviceSDK: string | null; //deviceSDK
	platform: string | null; //platform
	ipAddress: string | null;
	missingData?: string;
	mismatchedData?: Record<string, {
		previous: string;
		current: string
	}>;
	axonExtractedData?: any;
	idNumber2: string | null;
	idMatchScore: number;
	idPartialMatch: boolean;
	isLikelyOnFraudList: boolean;
	frontSpoofed: boolean;
	backSpoofed: boolean;
	unexpectedMediaEncountered: boolean;
}

export type Ref = {
	ref: string;
}

export type Response = {
	status: boolean;
	error: string;
	redirectUrl: string; //upon completion, redirect user to this url
	config: Config;
}

export type Config = {
	allowedIdTypes: AllowedIdType[];
	deviceKeyIdentifier: string;
	publicFaceScanEncryptionKey: string;
	productionKeyText: string;
	browserSdkLicense: string;
	url: string;
	transactionId: string;
	token: string;
	minMatchLevel: number;
	disableIdSpoofCheck: boolean;
	disableMissingInfoCheck: boolean;
	disableMismatchCheck: boolean;
	maxFailedRetries: number;
	furtherReviewMsgEnabled: boolean;
}

export type AllowedIdType = {
	groupName: string;
	templateTypes: string[];
	templateNames: string[];
	additionalDocs: AdditionalDoc[];
}

export type AdditionalDoc = {
	docTypeId: number;
	name: string;
}

export type Error = {
	errorType: ErrorType;
	data: Data;
	submitted?: boolean;
}

export enum ErrorType {
	FaceLivenessFailed = "FaceLivenessFailed", //Not a live face
	OcrTemplateMatchFailed = "OcrTemplateMatchFailed", // ID does not have a corresponding template 
	FaceDidNotMatchId = "FaceDidNotMatchId", //Portrait and face on ID do not match
	IdTypeNotAllowed = "IdTypeNotAllowed", //Id Type blocked based on Axon's server config's allowed ID Types
	IdScanFailed = "IdScanFailed",
	AxonMissingData = "AxonMissingData",
	AxonMismatchInformation = "AxonMismatchInformation", // When both front and back data don't match
	IdSpoofDetected = "IdSpoofDetected", // When the ID is a fake
	IDPartialMatch = "IDPartialMatch", // When the ID is a fake
	FraudListMatch = "FraudListMatch", // Matched against a fraud list
	SystemMatchFailed = "SystemMatchFailed", //Match failed against MRS
}

export const SkipErrorPageErrorTypes = [
	ErrorType.FraudListMatch,
	ErrorType.SystemMatchFailed,
	ErrorType.IdSpoofDetected,
];


export interface FaceTecResponse {
	success: boolean;
	wasProcessed: boolean;
	callData: {
		tid: string;
		path: string;
		date: string;
		epochSecond: number;
		requestMethod: string;
		customID: string | null;
	};
	additionalSessionData: {
		isAdditionalDataPartiallyIncomplete: boolean;
		platform: string | null;
		appID: string | null;
		installationID: string | null;
		deviceModel: string | null;
		deviceSDKVersion: string | null;
		sessionID: string | null;
		userAgent: string | null;
		ipAddress: string | null;
	};
	error: boolean;
	serverInfo: {
		coreServerSDKVersion: string;
		customOrStandardServerSDKVersion: string;
		type: string;
		mode: string;
		notice: string;
	};
	externalDatabaseRefID: string;
	matchLevel: number;
	fullIDStatusEnumInt: number;
	digitalIDSpoofStatusEnumInt: number;
	scanResultBlob: any;
	ocrResults: {
		ocrResults: {
			scanned: {
				groups: Array<{
					fields: Array<{
						fieldKey: string;
						uiFieldType: string;
						value: string;
					}>;
					groupFriendlyName: string;
					groupKey: string;
				}>;
			};
			templateName: string;
			templateType: string;
		};
	};
	documentData: {
		scannedValues: {
			groups: Array<{
				fields: Array<{
					fieldKey: string;
					uiFieldType: string;
					value: string;
				}>;
				groupFriendlyName: string;
				groupKey: string;
			}>;
		};
		templateInfo: {
			documentCountry: string;
			documentState: string;
			templateName: string;
			templateType: string;
		};
	};
	nfcStatusEnumInt: number;
	nfcAuthenticationStatusEnumInt: number;
	barcodeStatusEnumInt: number;
	mrzStatusEnumInt: number;
	faceOnDocumentStatusEnumInt: number;
	textOnDocumentStatusEnumInt: number;
	watermarkAndHologramStatusEnumInt: number;
	matchLevelNFCToFaceMap: number;
	isCompletelyDone: boolean;
	isIDScanIncomplete: boolean;
	isReadyForUserConfirmation: boolean;
	didCompleteIDScanWithoutMatchingOCRTemplate: boolean;
	didMatchIDScanToOCRTemplate: boolean;
	scannedIDPhotoFaceFoundWithMinimumQuality: boolean;
	unexpectedMediaEncounteredAtLeastOnce: boolean;
	photoIDNextStepEnumInt: number;
	idScanAgeV2GroupEnumInt: number;
	faceMapAgeV2GroupEnumInt: number;
	idScanSessionId: string;
	idScanAgeEstimateGroupV2EnumInt: number;
	idScanAgeEstimateGroupEnumInt: number;
	faceMapAgeEstimateV2GroupEnumInt: number;
	data: any;
	didCompleteIDScanWithUnexpectedMedia: boolean | null;
	didCompleteIDScanWithoutMatching: boolean | null;
	photoIDBackCrop: string | null;
	photoIDFrontCrop: string | null;
	photoIDFaceCrop: string | null;
	photoIDTamperingEvidenceFrontImage: string | null,
	photoIDPrimarySignatureCrop: string;
	photoIDSecondarySignatureCrop: string;
	extractedNFCImage: string;
	photoIDTamperingEvidenceBackImage: string;
	axonExtractedData: AxonExtractedData;
	axonMissingInformation: boolean;
	axonMismatchInformation: boolean;
	missingData: string;
	mismatchedData: Record<string, {
		previous: string;
		current: string
	}>;
	networkError: boolean;
	digitalIDSpoofDetected: boolean;
	isLikelyOnFraudList: boolean;
	axonInferencerResult: InferencerResult;
	frontSpoofed: boolean;
	backSpoofed: boolean;
}

export enum InferencerResultEnum {
	NoData = 'NoData',
	Valid = 'Valid',
	Invalid = 'Invalid',
}

export interface InferencerResult {
	frontProcessedInRequest: boolean;
	frontLive: InferencerResultEnum;

	backProcessedInRequest: boolean;
	backLive: InferencerResultEnum;
}

export interface AxonFlags {
	digitalSpoofTriggered: boolean;
	mrzChecksumErrorTriggered: boolean;
	missingInformation: boolean;
	frontAttempts: number;
	backAttempts: number;
	minMatchLevel: number;
	idNumbersFront: string[];
	idNumbersBack: string[];
	templatesFront: string[];
	templatesBack: string[];
}

export interface AxonExtractedData {
	firstName: string;
	lastName: string;
	fullName: string;
	nationality: string;
	nationalityNonMRZValue: string | null;
	dateOfBirth: string;
	placeOfBirth: string | null;
	idNumber: string;
	idNumber2: string | null;
	idBarcode: string | null;
	dateOfIssue: string | null;
	sex: string;
	address: string;
	ageOfId: string | null;
	countryCode: string | null;
	countryCodeNonMRZValue: string | null;
	dateOfExpiration: string;
	issuingAuthority: string | null;
}